import { supabase } from '../lib/supabaseClient';

/**
 * Services Service - Handles all community services operations
 */
export const servicesService = {
  // Expose the Supabase client for auth state listeners
  supabase: supabase,

  /**
   * Get current session from Supabase Auth
   */
  async getSession() {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      return session;
    } catch (error) {
      console.error('Error getting session:', error);
      return null;
    }
  },

  /**
   * Get current user from Supabase Auth
   */
  async getUser() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      return user;
    } catch (error) {
      console.error('Error getting user:', error);
      return null;
    }
  },

  /**
   * Clear session (sign out)
   * Enhanced for PWA compatibility - clears all auth-related storage
   */
  async clearSession() {
    try {
      console.log('[servicesService] Starting complete sign-out...');

      // Use global scope to ensure all contexts are cleared (important for PWA)
      await supabase.auth.signOut({ scope: 'global' });
      console.log('[servicesService] Supabase sign-out successful');

      // Clear all auth-related storage items (not just services_member_info)
      const storageKeys = Object.keys(localStorage);
      storageKeys.forEach(key => {
        if (key.includes('supabase') || key.includes('sb-') || key.includes('auth')) {
          console.log('[servicesService] Removing localStorage key:', key);
          localStorage.removeItem(key);
        }
      });
      localStorage.removeItem('services_member_info');

      // Clear sessionStorage (separate context in PWA standalone mode)
      try {
        sessionStorage.clear();
        console.log('[servicesService] sessionStorage cleared');
      } catch (e) {
        console.warn('[servicesService] Could not clear sessionStorage:', e);
      }

      console.log('[servicesService] Sign-out complete');
    } catch (error) {
      console.error('[servicesService] Error signing out:', error);
      throw error; // Re-throw to surface errors to caller
    }
  },

  /**
   * Get member info from localStorage
   */
  getMemberInfo() {
    const memberData = localStorage.getItem('services_member_info');
    if (memberData) {
      try {
        return JSON.parse(memberData);
      } catch (error) {
        console.error('Error parsing member info:', error);
      }
    }
    return null;
  },

  /**
   * Save member info to localStorage
   */
  saveMemberInfo(memberData) {
    localStorage.setItem('services_member_info', JSON.stringify(memberData));
  },

  /**
   * Get center ID from config
   */
  getCenterId() {
    // Get from window config or localStorage
    return window.config?.center_id || localStorage.getItem('center_id');
  },

  /**
   * Initiate phone verification via WhatsApp
   * Returns WhatsApp URL and session ID for polling
   */
  async initiateVerification(phoneNumber, centerId) {
    try {
      const { data, error } = await supabase.functions.invoke('initiate-phone-verification', {
        body: {
          phone_number: phoneNumber,
          center_id: centerId || this.getCenterId()
        }
      });

      if (error) throw error;

      // Store session ID for polling
      if (data.session_id) {
        localStorage.setItem('verification_session_id', data.session_id);
        localStorage.setItem('verification_phone', phoneNumber);
      }

      return data; // { verification_url, session_id, expires_in, bot_number }
    } catch (error) {
      console.error('Error initiating verification:', error);
      throw error;
    }
  },

  /**
   * Check verification status (for polling)
   */
  async checkVerificationStatus(sessionId, phoneNumber) {
    try {
      const { data, error } = await supabase.functions.invoke('check-verification-status', {
        body: {
          session_id: sessionId,
          phone_number: phoneNumber || localStorage.getItem('verification_phone'),
          center_id: this.getCenterId()
        }
      });

      if (error) throw error;

      // If verified, save session and member info
      if (data.verified && data.session_token) {
        this.saveSession({
          token: data.session_token,
          expires_at: data.session_expires,
          id: sessionId
        });

        // Clear verification session data
        localStorage.removeItem('verification_session_id');
        localStorage.removeItem('verification_phone');
      }

      if (data.member) {
        this.saveMemberInfo(data.member);
      }

      return data; // { verified, has_session, session_token, member, trust_level }
    } catch (error) {
      console.error('Error checking verification status:', error);
      throw error;
    }
  },

  /**
   * Send SMS verification code (legacy/fallback)
   * Now delegates to WhatsApp verification
   */
  async sendVerificationCode(phoneNumber, centerId) {
    // Delegate to WhatsApp verification for backward compatibility
    return this.initiateVerification(phoneNumber, centerId);
  },

  /**
   * Verify code (legacy/fallback)
   * With WhatsApp, verification happens automatically when user sends message
   * This method now just checks status
   */
  async verifyCode(phoneNumber, code, centerId, memberName, lastInitial) {
    // For backward compatibility, check verification status
    const sessionId = localStorage.getItem('verification_session_id');
    if (!sessionId) {
      throw new Error('No verification session found. Please initiate verification first.');
    }

    return this.checkVerificationStatus(sessionId, phoneNumber);
  },

  /**
   * Fetch service categories for a center
   * Auto-generates categories from services if service_categories table is empty
   * @param {string} centerId - Center ID
   * @param {string} serviceType - Service type ('offered' or 'needed') to filter categories
   */
  async getCategories(centerId, serviceType = null) {
    try {
      // Try to get from service_categories table first
      const { data, error } = await supabase
        .from('service_categories')
        .select('*')
        .eq('center_id', centerId)
        .eq('active', true)
        .order('display_order', { ascending: true });

      if (error) throw error;

      // If categories exist in table, use them
      if (data && data.length > 0) {
        return data.map(cat => ({
          id: cat.id,
          name: cat.category_name,
          icon: cat.icon
        }));
      }

      // FALLBACK: Auto-generate from actual services
      console.log(`[servicesService] No categories in table, auto-generating from ${serviceType || 'all'} services`);

      let query = supabase
        .from('community_services')
        .select('category')
        .eq('center_id', centerId)
        .eq('status', 'active');

      // Filter by service type if provided
      if (serviceType) {
        query = query.eq('service_type', serviceType);
      }

      const { data: services, error: servicesError } = await query;

      if (servicesError) throw servicesError;

      // Extract unique categories
      const uniqueCategories = [...new Set(services.map(s => s.category))].filter(Boolean);

      // Build category objects with structure expected by UI
      return uniqueCategories.map((catName) => ({
        id: catName.toLowerCase().replace(/\s+/g, '-').replace(/\//g, '-'),
        name: catName,
        icon: null // Icon will be looked up from serviceCategoryIcons.js in UI
      }));

    } catch (error) {
      console.error('Error fetching categories:', error);
      return [];
    }
  },

  /**
   * Fetch services for a center
   */
  async getServices(centerId, type = 'needed', category = null) {
    try {
      let query = supabase
        .from('community_services')
        .select(`
          *,
          member:community_members!member_id (
            id,
            first_name,
            last_initial,
            services_posted,
            services_completed
          )
        `)
        .eq('center_id', centerId)
        .eq('service_type', type)
        .eq('status', 'active');

      if (category) {
        query = query.eq('category', category);
      }

      // Sort by urgency for needed services, by created_at for offered
      if (type === 'needed') {
        query = query
          .order('urgency', { ascending: false })
          .order('needed_by', { ascending: true });
      } else {
        query = query.order('created_at', { ascending: false });
      }

      const { data, error } = await query;

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching services:', error);
      return [];
    }
  },

  /**
   * Create a new service post
   * Includes retry logic to handle race conditions after magic link login
   */
  async createService(serviceData) {
    try {
      // Retry logic: poll for session with exponential backoff
      // This handles the case where user clicks "Create Service" immediately after magic link login
      let session = await this.getSession();
      let retries = 0;
      const maxRetries = 3;
      const retryDelays = [100, 250, 500]; // ms

      while (!session && retries < maxRetries) {
        console.debug(`[servicesService] No session found, retrying (${retries + 1}/${maxRetries})...`);
        // eslint-disable-next-line no-loop-func
        await new Promise(resolve => setTimeout(resolve, retryDelays[retries]));
        session = await this.getSession();
        retries++;
      }

      if (!session) {
        throw new Error('Not authenticated. Please sign in and try again.');
      }

      console.debug('[servicesService] Session confirmed, creating service...');
      const { data, error } = await supabase.functions.invoke('create-service', {
        headers: {
          Authorization: `Bearer ${session.access_token}`
        },
        body: {
          service: serviceData
        }
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating service:', error);
      throw error;
    }
  },

  /**
   * Update a service (mark as fulfilled, remove, etc.)
   */
  async updateService(serviceId, updates) {
    try {
      const session = await this.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      const { data, error } = await supabase.functions.invoke('update-service', {
        body: {
          service_id: serviceId,
          updates: updates
        }
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating service:', error);
      throw error;
    }
  },

  /**
   * Log a contact attempt (legacy - delegates to logContactAnalytics)
   */
  async logContact(serviceId, method) {
    try {
      const session = await this.getSession();
      const user = await this.getUser();

      const { data, error} = await supabase.functions.invoke('log-contact', {
        body: {
          service_id: serviceId,
          contact_method: method,
          user_id: user?.id || null
        }
      });

      if (error) {
        console.error('Error logging contact:', error);
      }
      return data;
    } catch (error) {
      console.error('Error in logContact:', error);
    }
  },

  /**
   * Log contact analytics for category popularity tracking
   */
  async logContactAnalytics(serviceId, contactMethod, category, centerId) {
    try {
      const memberInfo = this.getMemberInfo();

      const { data, error } = await supabase
        .from('service_contact_analytics')
        .insert({
          service_id: serviceId,
          member_id: memberInfo?.id || null,
          contact_method: contactMethod, // 'whatsapp', 'call', or 'sms'
          category: category,
          center_id: centerId || this.getCenterId()
        })
        .select()
        .single();

      if (error) {
        console.error('Error logging contact analytics:', error);
        // Don't throw - analytics failure shouldn't block contact
      }
      return data;
    } catch (error) {
      console.error('Error in logContactAnalytics:', error);
      // Silent fail for analytics
    }
  },

  /**
   * Get category popularity for a center (last 30 days)
   */
  async getCategoryPopularity(centerId) {
    try {
      const { data, error } = await supabase
        .from('category_popularity')
        .select('*')
        .eq('center_id', centerId || this.getCenterId())
        .order('total_clicks', { ascending: false });

      if (error) {
        console.error('Error fetching category popularity:', error);
        return [];
      }
      return data || [];
    } catch (error) {
      console.error('Error in getCategoryPopularity:', error);
      return [];
    }
  },

  /**
   * Report a service
   */
  async reportService(serviceId, reason, details) {
    try {
      const session = this.getSession();
      const { data, error } = await supabase
        .from('service_reports')
        .insert({
          service_id: serviceId,
          report_reason: reason,
          report_details: details,
          reported_by_session: session?.id || `anonymous_${Date.now()}`
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error reporting service:', error);
      throw error;
    }
  },

  /**
   * Get member's own services
   */
  async getMyServices() {
    try {
      const session = this.getSession();
      const memberInfo = this.getMemberInfo();
      
      if (!session || !memberInfo) {
        return [];
      }

      const { data, error } = await supabase
        .from('community_services')
        .select('*')
        .eq('member_id', memberInfo.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching my services:', error);
      return [];
    }
  }
};

export default servicesService;